import React, { useState, useEffect, useContext } from 'react';
import { collection, doc, updateDoc, deleteDoc, query, onSnapshot } from 'firebase/firestore';
import { FirebaseContext } from '../App';
import { firebaseAppId } from '../configs/firebaseConfig';

const TransactionList = () => {
    const { db, userId } = useContext(FirebaseContext);
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterType, setFilterType] = useState('すべて');
    const [searchTerm, setSearchTerm] = useState('');
    const [showEditModal, setShowEditModal] = useState(false);
    const [currentTransaction, setCurrentTransaction] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleteTransactionId, setDeleteTransactionId] = useState(null);
    const [modalMessage, setModalMessage] = useState('');
    const [modalMessageType, setModalMessageType] = useState('');

    const expenseCategories = ['仕入', '消耗品費', '交通費', '通信費', '水道光熱費', '地代家賃', '福利厚生費', '旅費交通費', '広告宣伝費', '接待交際費', '租税公課', '減価償却費', '修繕費', '新聞図書費', '会議費', '雑費', '事業主貸'];
    const incomeCategories = ['売上', '事業主借', '元入金', 'その他収入'];

    useEffect(() => {
        if (!db || !userId) return;

        setLoading(true);
        const transactionsColRef = collection(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`);
        const q = query(transactionsColRef);

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const fetchedTransactions = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            fetchedTransactions.sort((a, b) => b.date.toDate() - a.date.toDate());
            setTransactions(fetchedTransactions);
            setLoading(false);
        }, (error) => {
            console.error("取引の取得エラー:", error);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [db, userId]);

    const filteredTransactions = transactions.filter(transaction => {
        const matchesType = filterType === 'すべて' || transaction.type === filterType;
        const matchesSearch = searchTerm === '' ||
            transaction.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
            transaction.memo.toLowerCase().includes(searchTerm.toLowerCase());
        return matchesType && matchesSearch;
    });

    const handleEditClick = (transaction) => {
        setCurrentTransaction({
            ...transaction,
            date: transaction.date.toDate().toISOString().split('T')[0]
        });
        setShowEditModal(true);
        setModalMessage('');
    };

    const handleDeleteClick = (id) => {
        setDeleteTransactionId(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!db || !userId || !deleteTransactionId) return;
        setModalMessage('');
        try {
            const docRef = doc(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`, deleteTransactionId);
            await deleteDoc(docRef);
            setModalMessage('取引が削除されました。');
            setModalMessageType('success');
        } catch (err) {
            console.error("ドキュメントの削除エラー:", err);
            setModalMessage('取引の削除中にエラーが発生しました: ' + err.message);
            setModalMessageType('error');
        } finally {
            setShowDeleteConfirm(false);
            setDeleteTransactionId(null);
            setTimeout(() => setModalMessage(''), 3000);
        }
    };

    const handleUpdateSubmit = async (e) => {
        e.preventDefault();
        if (!db || !userId || !currentTransaction) return;

        setModalMessage('');
        if (!currentTransaction.date || !currentTransaction.type || !currentTransaction.category || !currentTransaction.amount || !currentTransaction.description || !currentTransaction.paymentMethod) {
            setModalMessage('全ての必須項目を入力してください。');
            setModalMessageType('error');
            return;
        }

        if (isNaN(currentTransaction.amount) || parseFloat(currentTransaction.amount) <= 0) {
            setModalMessage('金額は正の数で入力してください。');
            setModalMessageType('error');
            return;
        }

        try {
            const docRef = doc(db, `artifacts/${firebaseAppId}/users/${userId}/transactions`, currentTransaction.id);
            await updateDoc(docRef, {
                date: new Date(currentTransaction.date),
                type: currentTransaction.type,
                category: currentTransaction.category,
                amount: parseFloat(currentTransaction.amount),
                description: currentTransaction.description,
                paymentMethod: currentTransaction.paymentMethod,
                memo: currentTransaction.memo,
            });
            setModalMessage('取引が更新されました！');
            setModalMessageType('success');
            setShowEditModal(false);
            setCurrentTransaction(null);
            setTimeout(() => setModalMessage(''), 3000);
        } catch (err) {
            console.error("取引の更新エラー:", err);
            setModalMessage('取引の更新中にエラーが発生しました: ' + err.message);
            setModalMessageType('error');
        }
    };

    const EditTransactionModal = () => (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-lg relative">
                <h3 className="text-2xl font-semibold text-gray-800 mb-6 border-b pb-4">取引を編集</h3>
                {modalMessage && (
                    <div className={`p-3 rounded-md mb-4 text-sm ${modalMessageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {modalMessage}
                    </div>
                )}
                {currentTransaction && (
                    <form onSubmit={handleUpdateSubmit} className="space-y-4">
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">日付</label>
                            <input
                                type="date"
                                value={currentTransaction.date}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, date: e.target.value })}
                                className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">種類</label>
                            <select
                                value={currentTransaction.type}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, type: e.target.value, category: '' })}
                                className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            >
                                <option value="支出">支出</option>
                                <option value="収入">収入</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">勘定科目</label>
                            <select
                                value={currentTransaction.category}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, category: e.target.value })}
                                className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            >
                                <option value="" disabled>選択してください</option>
                                {currentTransaction.type === '支出' ? (
                                    expenseCategories.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))
                                ) : (
                                    incomeCategories.map((cat) => (
                                        <option key={cat} value={cat}>{cat}</option>
                                    ))
                                )}
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">金額 (円)</label>
                            <input
                                type="number"
                                value={currentTransaction.amount}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, amount: e.target.value })}
                                className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                                min="1"
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">内容（詳細）</label>
                            <input
                                type="text"
                                value={currentTransaction.description}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, description: e.target.value })}
                                className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">支払い方法 / 入金元</label>
                            <select
                                value={currentTransaction.paymentMethod}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, paymentMethod: e.target.value })}
                                className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                required
                            >
                                <option value="現金">現金</option>
                                <option value="普通預金">普通預金</option>
                                <option value="クレジットカード">クレジットカード</option>
                                <option value="売掛金">売掛金</option>
                                <option value="買掛金">買掛金</option>
                            </select>
                        </div>
                        <div>
                            <label className="block text-gray-700 text-sm font-bold mb-1">メモ (任意)</label>
                            <textarea
                                value={currentTransaction.memo}
                                onChange={(e) => setCurrentTransaction({ ...currentTransaction, memo: e.target.value })}
                                className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                                rows="2"
                            ></textarea>
                        </div>
                        <div className="flex justify-end gap-3 mt-6">
                            <button
                                type="button"
                                onClick={() => setShowEditModal(false)}
                                className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition duration-200 ease-in-out shadow-md"
                            >
                                キャンセル
                            </button>
                            <button
                                type="submit"
                                className="px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 ease-in-out shadow-md"
                            >
                                更新
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );

    const DeleteConfirmModal = () => (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-sm text-center">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">本当に削除しますか？</h3>
                <p className="text-gray-600 mb-6">この操作は元に戻せません。</p>
                <div className="flex justify-center gap-4">
                    <button
                        onClick={() => setShowDeleteConfirm(false)}
                        className="px-6 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition duration-200 ease-in-out shadow-md"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={confirmDelete}
                        className="px-6 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition duration-200 ease-in-out shadow-md"
                    >
                        削除
                    </button>
                </div>
            </div>
        </div>
    );


    return (
        <div className="bg-white rounded-xl shadow-lg p-8">
            <h2 className="text-3xl font-semibold text-gray-800 mb-6 border-b pb-4">取引一覧</h2>
            {modalMessage && (
                <div className={`p-4 rounded-md mb-4 ${modalMessageType === 'success' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {modalMessage}
                </div>
            )}
            <div className="flex flex-col sm:flex-row gap-4 mb-6">
                <div className="flex-1">
                    <label htmlFor="filterType" className="sr-only">種類で絞り込み</label>
                    <select
                        id="filterType"
                        value={filterType}
                        onChange={(e) => setFilterType(e.target.value)}
                        className="shadow border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                    >
                        <option value="すべて">すべて</option>
                        <option value="収入">収入</option>
                        <option value="支出">支出</option>
                    </select>
                </div>
                <div className="flex-1">
                    <label htmlFor="searchTerm" className="sr-only">検索</label>
                    <input
                        type="text"
                        id="searchTerm"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="内容や勘定科目で検索"
                        className="shadow appearance-none border rounded-lg w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:ring-2 focus:ring-blue-400"
                        required
                    />
                </div>
            </div>

            {loading ? (
                <div className="text-center text-gray-600">取引を読み込み中...</div>
            ) : filteredTransactions.length === 0 ? (
                <p className="text-center text-gray-600 py-8">表示する取引がありません。</p>
            ) : (
                <>
                    {/* モバイル表示用（カード形式） */}
                    <div className="md:hidden space-y-4">
                        {filteredTransactions.map((transaction) => (
                            <div key={transaction.id} className={`bg-white rounded-lg shadow-md p-4 border-l-4 ${transaction.type === '収入' ? 'border-green-500' : 'border-red-500'}`}>
                                <div className="flex justify-between items-center mb-2">
                                    <span className="text-gray-500 text-sm">{transaction.date.toDate().toLocaleDateString('ja-JP')}</span>
                                    <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.type === '収入' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                        {transaction.type}
                                    </span>
                                </div>
                                <div className="text-lg font-bold text-gray-800 mb-1">
                                    ¥{transaction.amount.toLocaleString()}
                                </div>
                                <div className="text-gray-700 font-medium mb-1">
                                    {transaction.category} - {transaction.description}
                                </div>
                                <div className="text-gray-600 text-sm mb-3">
                                    支払い方法: {transaction.paymentMethod}
                                </div>
                                <div className="flex justify-end gap-2">
                                    <button
                                        onClick={() => handleEditClick(transaction)}
                                        className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                                    >
                                        編集
                                    </button>
                                    <button
                                        onClick={() => handleDeleteClick(transaction.id)}
                                        className="text-red-600 hover:text-red-900 text-sm font-medium"
                                    >
                                        削除
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* PC表示用（テーブル形式） */}
                    <div className="hidden md:block overflow-x-auto rounded-lg shadow-sm border border-gray-200">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">日付</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">種類</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">勘定科目</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">内容</th>
                                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">金額 (円)</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">支払い方法</th>
                                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">操作</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredTransactions.map((transaction) => (
                                    <tr key={transaction.id}>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.date.toDate().toLocaleDateString('ja-JP')}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${transaction.type === '収入' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {transaction.type}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.category}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.description}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-right text-gray-900">{transaction.amount.toLocaleString()}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{transaction.paymentMethod}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEditClick(transaction)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                title="編集"
                                            >
                                                編集
                                            </button>
                                            <button
                                                onClick={() => handleDeleteClick(transaction.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="削除"
                                            >
                                                削除
                                            </button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </>
            )}

            {showEditModal && <EditTransactionModal />}
            {showDeleteConfirm && <DeleteConfirmModal />}
        </div>
    );
};

export default TransactionList;
